import styles from "./StockAnalysts.module.scss";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { getStockAnalystsBasics, getStockAnalystsEstimatesEPS, getStockAnalystsRecent, getStockAnalystsTargetPrice, getAnalystOpinions } from "@/controllers/stock-analysts";
import { StockAnalystsBasics } from "@/models/stock-analysts";
import { classNames, nFormatter } from "@/lib/utils";
import { usExchanges } from "@/lib/vars";
import IndicatorBar from "@/components/indicator-bar/IndicatorBar";
import Module from "@/components/module/Module";
import { getChartDataNew } from "@/controllers/chart-data";
import { Stock } from "@/models/stock";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import {
    backgroundColorState,
    blackLighterState,
    blackState,
    greyDarkerState,
    greyLighterState,
    greyState,
    lighterBackgroundColorState,
    negativeState,
    neutralState,
    positiveState,
    textColorState,
    ortexColorState,
    themeState,
    whiteDarkerState,
    userState,
} from "@/lib/store";
import UpgradePlan from "@/components/upgrade-plan/UpgradePlan";
import { Box, Typography, Avatar, Chip, useMediaQuery } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Skeleton from "../skeleton/Skeleton";
import ClampedNote from "@/components/clamped-note/ClampedNote";
import { DataGrid } from "../data-grid/DataGrid";

const StockAnalysts: FC<{ stock: Stock }> = ({ stock }) => {
    const theme = useAtomValue(themeState);
    const grey = useAtomValue(greyState);
    const black = useAtomValue(blackState);
    const neutral = useAtomValue(neutralState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const textColor = useAtomValue(textColorState);
    const greyDarker = useAtomValue(greyDarkerState);
    const greyLighter = useAtomValue(greyLighterState);
    const whiteDarker = useAtomValue(whiteDarkerState);
    const blackLighter = useAtomValue(blackLighterState);
    const ortexColor = useAtomValue(ortexColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const user = useAtomValue(userState);

    const [isLoading, setIsLoading] = useState(true);

    const [opinions, setOpinions] = useState<{ bull_case: string; bear_case: string } | null>(null);
    const [opinionsLoading, setOpinionsLoading] = useState(true);
    const [opinionsObfuscated, setOpinionsObfuscated] = useState(false);

    const [chartShortened, setChartShortened] = useState(false);
    const [chartObfuscated, setChartObfuscated] = useState(false);

    const [analystsBasics, setAnalystsBasics] = useState<StockAnalystsBasics>();
    const [analystsBasicsObfuscated, setAnalystsBasicsObfuscated] = useState(false);

    const [sonification] = useState<boolean>(!!user.settings.module_settings["Sonification->/settings"]?.value);

    const [price, setPrice] = useState<[number, number][]>([]);
    const [volume, setVolume] = useState<[number, number][]>([]);
    const [target, setTarget] = useState<[number, number][]>([]);
    const [eps, setEps] = useState<[number, number][]>([]);
    const isTablet = useMediaQuery("(max-width:1280px)");

    // On a phone the list of recent ratings holds a loaded list's room until its first page is in
    // (StockAnalysts.module.scss), so the chart below starts below the fold instead of in view and
    // pushed off by the rows (CLS). Let go a moment after the page lands, once the rows have drawn.
    // One timer for the release: cleared on a stock change and on unmount, so an old stock's answer
    // cannot release the new stock's room. The room is never held longer than 6 s.
    const [tableLoaded, setTableLoaded] = useState(false);
    const releaseTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const releaseIn = (ms: number) => {
        clearTimeout(releaseTimer.current);
        releaseTimer.current = setTimeout(() => setTableLoaded(true), ms);
    };
    useEffect(() => {
        setTableLoaded(false);
        releaseIn(6000);
        return () => clearTimeout(releaseTimer.current);
    }, [stock.stock_id]);
    // Only the shown stock's answer releases the room: an aborted request for the previous stock
    // still settles, and would otherwise cut the new stock's hold short.
    const shownStock = useRef(stock.stock_id);
    shownStock.current = stock.stock_id;
    const fetchRecent = useCallback(
        (params: Parameters<typeof getStockAnalystsRecent>[0]) =>
            getStockAnalystsRecent(params).finally(() => {
                if (String(params.universe_id) === String(shownStock.current)) releaseIn(1000);
            }),
        []
    );
    const isUSStock = usExchanges.includes(stock.exchangesymbol);
    // A stock with no coverage comes back with the counts missing/null/0, so there is nothing to show
    // in the consensus row (the IndicatorBar would sit at an arbitrary position too).
    const hasAnalystRatings = !!(analystsBasics?.noof_sell || analystsBasics?.noof_hold || analystsBasics?.noof_buy);
    // The chips still render without data — greyed out with a dash — so the user can see the field exists
    // for other stocks. 0 is not a real target price, but a 0% return potential is (target == current price),
    // so that one only needs to be a finite number.
    const hasTargetPrice = !!analystsBasics?.target_price;
    const hasReturnPotential = Number.isFinite(analystsBasics?.return_potential);
    const noDataPlaceholder = "—";

    useEffect(() => {
        let cancelled = false;

        setOpinionsLoading(true);
        getAnalystOpinions(stock.tradingitem_id).then((data) => {
            if (cancelled) return;
            setOpinions(data);
            setOpinionsObfuscated(!!data?.obfuscated);
            setOpinionsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [stock]);

    useEffect(() => {
        let cancelled = false;
        getStockAnalystsBasics(stock.stock_id).then((res) => {
            if (cancelled) return;
            setAnalystsBasicsObfuscated(res.obfuscated);
            setAnalystsBasics(res);
            setIsLoading(false);
        });

        const today = dayjs();
        const fiveYearsAgo = today.subtract(5, "years");
        const todayFormatted = today.format("YYYY-MM-DD");
        const fiveYearsAgoFormatted = fiveYearsAgo.format("YYYY-MM-DD");

        Promise.all([
            getChartDataNew(stock.tradingitem_id, "ohlcv", "2015-01-01", todayFormatted),
            getStockAnalystsTargetPrice(stock.stock_id, fiveYearsAgoFormatted, todayFormatted),
            getStockAnalystsEstimatesEPS(stock.stock_id, fiveYearsAgoFormatted, todayFormatted),
        ]).then((res) => {
            if (cancelled) return;
            setChartObfuscated(res.some((data) => data["obfuscated"]));
            setChartShortened(res.some((data) => data["ts_shortened"]));
            setPrice((res[0] ?? []).map((row) => [row[0], row[4]] as [number, number]));
            setVolume((res[0] ?? []).map((row) => [row[0], row[row.length - 1]] as [number, number]));
            setTarget(res[1].target_price ?? []);
            setEps(res[2].estimates_eps_12mfwd ?? []);
        });

        return () => {
            cancelled = true;
        };
    }, [stock]);

    const chartSeries = useMemo<TimeSeries[]>(
        () => [
            { id: "price", title: `${stock.ticker} Price in ${stock.currency}`, type: "line", data: price, color: positive, axis: "right", precision: 2 },
            { id: "target_price", title: "Target Price", type: "line", data: target, color: neutral, axis: "right", precision: 2 },
            { id: "estimates_eps_12mfwd", title: "EPS 12m fwd", type: "line", data: eps, color: negative, axis: "left", precision: 2 },
            { id: "volume", title: "Volume", type: "column", data: volume, color: "dodgerblue", pane: "volume", format: "compact" },
        ],
        [price, target, eps, volume, positive, neutral, negative, stock.ticker, stock.currency]
    );

    return (
        <div className={classNames(styles[theme], styles.StockAnalysts)}>
            <style jsx>{`
                .${styles.basics} > div {
                    color: ${black};
                    border: 1px solid ${grey};
                }
            `}</style>

            <div className={styles.analysts}>
                <Module
                    title={
                        <>
                            Analysts
                            <>
                                {analystsBasics && (
                                    <>
                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            disabled={!hasTargetPrice}
                                            className={!hasTargetPrice ? undefined : analystsBasics.return_potential < 0 ? "bc-negative" : "bc-positive"}
                                            sx={{
                                                marginRight: 1,
                                                marginLeft: 1,
                                            }}
                                            label={
                                                <div style={{ display: "flex" }}>
                                                    <Typography variant="body2" sx={{ paddingRight: "5px", alignSelf: "center" }} className={!hasTargetPrice ? undefined : analystsBasics.return_potential < 0 ? "negative" : "positive"}>
                                                        Target Price
                                                    </Typography>

                                                    <Typography
                                                        variant="body1"
                                                        className={classNames(!hasTargetPrice ? "" : analystsBasics.return_potential < 0 ? "negative" : "positive", hasTargetPrice && analystsBasicsObfuscated ? "obfuscated" : "")}
                                                        sx={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {hasTargetPrice ? nFormatter(analystsBasics.target_price, 2) : noDataPlaceholder}
                                                    </Typography>
                                                </div>
                                            }
                                        />

                                        <Chip
                                            variant="outlined"
                                            size="small"
                                            disabled={!hasReturnPotential}
                                            className={!hasReturnPotential ? undefined : analystsBasics.return_potential < 0 ? "bc-negative" : "bc-positive"}
                                            sx={{
                                                marginRight: 1,
                                                marginLeft: 1,
                                            }}
                                            label={
                                                <div style={{ display: "flex" }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            paddingRight: "5px",
                                                            alignSelf: "center",
                                                        }}
                                                        className={!hasReturnPotential ? undefined : analystsBasics.return_potential < 0 ? "negative" : "positive"}
                                                    >
                                                        Return Potential
                                                    </Typography>

                                                    <Typography
                                                        variant="body1"
                                                        className={classNames(!hasReturnPotential ? "" : analystsBasics.return_potential < 0 ? "negative" : "positive", hasReturnPotential && analystsBasicsObfuscated ? "obfuscated" : "")}
                                                        sx={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {hasReturnPotential ? analystsBasics.return_potential + "%" : noDataPlaceholder}
                                                    </Typography>
                                                </div>
                                            }
                                        />
                                    </>
                                )}
                            </>
                        </>
                    }
                    help={{ moduleId: "analysts", modulePage: "V2_stock_analysts" }}
                    allowFullscreen
                >
                    <div className={styles["analysts-inner"]}>
                        {isLoading ? (
                            // The consensus row's own height, so the cards below don't drop when it lands.
                            <Box sx={{ height: "55px" }}>
                                <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
                            </Box>
                        ) : (
                            analystsBasics &&
                            (!hasAnalystRatings ? (
                                <Box className={styles["analysts-content"]} sx={{ height: "55px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography variant="body2" sx={{ color: grey }}>
                                        No analyst ratings
                                    </Typography>
                                </Box>
                            ) : (
                                <Box className={styles["analysts-content"]} sx={{ height: "55px" }}>
                                    <Box sx={{ width: "100%" }}>
                                        <Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                                                <Typography variant="body1" sx={{ marginY: 1 }}>
                                                    Analyst consensus
                                                </Typography>
                                                <Chip
                                                    sx={{
                                                        borderColor: negative,
                                                        "& .MuiChip-label": {
                                                            color: negative,
                                                        },
                                                        "& .MuiChip-root": {
                                                            border: negative,
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    avatar={
                                                        <Avatar sx={{ textcolor: textColor, bgcolor: "transparent" }}>
                                                            <Typography
                                                                variant="body2"
                                                                color={negative}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {analystsBasics.noof_sell ?? "-"}
                                                            </Typography>
                                                        </Avatar>
                                                    }
                                                    label={<Typography variant="body2">SELL</Typography>}
                                                />

                                                <Chip
                                                    variant="outlined"
                                                    avatar={
                                                        <Avatar sx={{ bgcolor: "transparent" }}>
                                                            <Typography
                                                                variant="body2"
                                                                color={textColor}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {analystsBasics.noof_hold ?? "-"}
                                                            </Typography>
                                                        </Avatar>
                                                    }
                                                    label={<Typography variant="body2">HOLD</Typography>}
                                                />

                                                <Chip
                                                    sx={{
                                                        borderColor: positive,
                                                        "& .MuiChip-label": {
                                                            color: positive,
                                                        },
                                                        "& .MuiChip-root": {
                                                            border: positive,
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    avatar={
                                                        <Avatar sx={{ textcolor: textColor, bgcolor: "transparent" }}>
                                                            <Typography
                                                                variant="body2"
                                                                color={positive}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {analystsBasics.noof_buy ?? "-"}
                                                            </Typography>
                                                        </Avatar>
                                                    }
                                                    label={<Typography variant="body2">BUY</Typography>}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>

                                    <IndicatorBar percentage={analystsBasics.consensus_number} />
                                </Box>
                            ))
                        )}
                        {isUSStock && (opinionsLoading || opinions?.bull_case || opinions?.bear_case) && (
                            <div className={styles.opinionsCard}>
                                <div className={styles.opinionsSide}>
                                    <div className={styles.opinionsHeader}>
                                        <TrendingUpIcon fontSize="small" className={styles.bullIcon} />
                                        <Typography variant="caption" className={styles.opinionsLabel} sx={{ color: positive }}>
                                            Bulls Say
                                        </Typography>
                                    </div>
                                    <ClampedNote>
                                        {opinionsLoading || opinions?.bull_case === "" ? (
                                            <Typography variant="body2" className={styles.opinionsGenerating} sx={{ color: textColor }}>
                                                Generating AI summary of the Analyst data...
                                            </Typography>
                                        ) : opinions?.bull_case ? (
                                            <Typography variant="body2" className={classNames(styles.opinionsText, opinionsObfuscated ? "obfuscated" : "")} sx={{ color: textColor }}>
                                                {opinions.bull_case}
                                            </Typography>
                                        ) : null}
                                    </ClampedNote>
                                </div>
                                <div className={styles.opinionsDivider} />
                                <div className={styles.opinionsSide}>
                                    <div className={styles.opinionsHeader}>
                                        <TrendingDownIcon fontSize="small" className={styles.bearIcon} />
                                        <Typography variant="caption" className={styles.opinionsLabel} sx={{ color: negative }}>
                                            Bears Say
                                        </Typography>
                                    </div>
                                    <ClampedNote>
                                        {opinionsLoading || opinions?.bear_case === "" ? (
                                            <Typography variant="body2" className={styles.opinionsGenerating} sx={{ color: textColor }}>
                                                Generating AI summary of the Analyst data...
                                            </Typography>
                                        ) : opinions?.bear_case ? (
                                            <Typography variant="body2" className={classNames(styles.opinionsText, opinionsObfuscated ? "obfuscated" : "")} sx={{ color: textColor }}>
                                                {opinions.bear_case}
                                            </Typography>
                                        ) : null}
                                    </ClampedNote>
                                </div>
                            </div>
                        )}

                        <div className={classNames(styles.table, tableLoaded ? "" : styles.tableLoading)}>
                            <DataGrid id={stock.stock_id} fetcher={fetchRecent} />
                        </div>
                    </div>
                </Module>
            </div>

            <div className={styles.chart}>
                <Module
                    title={`${stock.exchangesymbol}:${stock.ticker} Price, Target Price and EPS Chart`}
                    help={{ moduleId: "analysts_chart", modulePage: "V2_stock_analysts" }}
                    allowFullscreen
                    noPadding={{ source: "body", type: "mobile" }}
                    feedback
                >
                    <div className={classNames(styles["chart-container"], chartObfuscated && !chartShortened && "obfuscated")}>
                        {chartShortened && <UpgradePlan />}
                        <TimeSeriesChart series={chartSeries} paneHeights={{ volume: 0.35 }} initialRange="1y" exportName={`${stock.ticker}-analysts`} />
                    </div>
                </Module>
            </div>
        </div>
    );
};

export default StockAnalysts;
